export const LOCALES = [
  { value: 'en', flag: '🇬🇧', nativeLabel: 'English' },
  { value: 'pl', flag: '🇵🇱', nativeLabel: 'Polski' },
  { value: 'de', flag: '🇩🇪', nativeLabel: 'Deutsch' },
  { value: 'uk', flag: '🇺🇦', nativeLabel: 'Українська' },
] as const

export type Locale = (typeof LOCALES)[number]['value']
export type LocaleOption = (typeof LOCALES)[number]
export const LOCALE_CODES: [Locale, ...Locale[]] = ['en', 'pl', 'de', 'uk']

const en = {
  nav: {
    synthesize: 'Synthesize',
    voices: 'Voices',
    history: 'History',
  },
  settings: {
    title: 'Settings',
    appearance: 'Appearance',
    theme: 'Theme',
    themeDesc: 'Choose how the interface looks',
    light: 'Light',
    dark: 'Dark',
    system: 'System',
    language: 'Language',
    languageDesc: 'Choose the interface language',
    sessionHistory: 'Session history',
    sessionHistoryDesc: 'Number of waveforms to keep in the synthesis session',
    autoPlayStreaming: 'Auto-play while generating',
    autoPlayStreamingDesc: 'Stream audio to speakers as it is being synthesised. Disable to buffer silently and play manually.',
    download: 'Download',
    downloadFormatLabel: 'Default format',
    downloadFormatDesc: 'Default audio format used in the download dialog',
    downloadSampleRateLabel: 'Default sample rate',
    downloadSampleRateDesc: 'Default sample rate for downloaded audio',
    downloadBitDepthLabel: 'Default WAV bit depth',
    downloadBitDepthDesc: 'Default bit depth for WAV downloads',
    downloadBitRateLabel: 'Default MP3 bit rate',
    downloadBitRateDesc: 'Default bit rate for MP3 downloads',
  },
  synthesis: {
    pageTitle: 'Text-to-Speech',
    voiceLabel: 'Voice',
    noVoice: 'No reference voice',
    styleLabel: 'Style',
    noStyle: 'default',
    languageLabel: 'Language',
    noLanguage: '— no language —',
    textLabel: 'Text',
    textPlaceholder: 'Enter text to synthesize',
    normalizeText: 'Normalize text',
    normalizeHint: '(LLM pre-processing)',
    normalizedPreviewLabel: 'Normalized text sent to TTS',
    ruleNormalizeText: 'Dictionary normalization',
    ruleNormalizeHint: '(rule-based pre-processing)',
    ruleNormalizedPreviewLabel: 'After dictionary normalization',
    advanced: 'Advanced options',
    speed: 'Speed',
    controlInstruction: 'Control Instruction',
    controlPlaceholder: 'e.g. Speak slowly and clearly',
    modeLabel: 'Voice mode',
    modeStrict: 'Strict',
    modeCreative: 'Creative',
    btnNormalizing: 'Normalizing text…',
    btnRuleNormalizing: 'Applying dictionary rules…',
    btnSynthesizing: 'Synthesizing…',
    btnLoading: 'Loading model…',
    btnSynthesize: '▶ Synthesize',
    errNormalizationFailed: 'Normalization failed',
    errRuleNormalizationFailed: 'Dictionary normalization failed',
    errNormalizerUnavailable: 'Normalizer service unavailable',
    errRuleNormalizerUnavailable: 'Dictionary normalizer unavailable',
    errRuleNormalizationEmpty: 'Dictionary normalization returned empty text — check the input',
    errNormalizationEmpty: 'LLM normalization returned empty text — check the input',
    errRuleNormalizeNeedsLanguage: 'Dictionary normalization needs a supported language (Polish, English, German, Spanish or Ukrainian)',
    errServiceUnavailable: 'Service unavailable',
    clearAll: 'Clear all',
  },
  voices: {
    pageTitle: 'Voice Management',
    requiredRate: 'Required sample rate',
    dropZone: 'Drop audio file here or click to browse',
    resampling: 'Resampling',
    resampled: 'Resampled',
    nameLabel: 'Voice name (optional)',
    namePlaceholder: 'e.g. Speaker 1',
    transcriptionLabel: 'Reference transcription (optional)',
    transcriptionPlaceholder: 'Enter the exact text spoken in the audio…',
    defaultSpeedLabel: 'Default speed (optional)',
    defaultLanguageLabel: 'Default language',
    noDefaultLanguage: 'No default language',
    languageExclusive: 'Language exclusive',
    languageExclusiveHint: 'only available when {lang} is selected',
    btnUpload: 'Upload Voice',
    btnUploading: 'Uploading…',
    btnResampling: 'Resampling…',
    btnCancel: 'Cancel',
    noVoices: 'No voices uploaded yet',
    errResamplingFailed: 'Resampling failed — please convert the file manually',
    errUploadFailed: 'Upload failed',
    errUploadUnavailable: 'Upload failed — service unavailable',
    errPlaybackFailed: 'Playback failed',
    errPlaybackUnavailable: 'Playback failed — service unavailable',
    ariaPlay: 'Play',
    ariaStop: 'Stop',
    ariaDelete: 'Delete',
    stylesBtn: 'Styles',
    uploadTab: 'Upload file',
    recordTab: 'Record audio',
    recordStart: 'Click to start recording',
    recording: 'Recording',
    recordStop: 'Stop',
    recordProcessing: 'Processing recording…',
    recordErrMic: 'Microphone access denied',
    errAudioTooLong: 'Audio too long — maximum {max} s',
  },
  player: {
    buffering: 'Buffering…',
    streaming: 'Streaming',
    playback: 'Playback',
    error: 'Error',
    downloadWav: 'Download WAV',
    playbackInterrupted: 'Playback interrupted',
    playFailed: 'Could not play audio ({reason})',
    audioBlocked: 'The browser blocked live audio. Press ▶ when streaming finishes.',
    ariaPlay: 'Play',
    ariaPause: 'Pause',
    curlTitle: 'cURL command',
    curlNote: 'Targets the TTS service directly. Replace localhost:8000 with your server address.',
    curlCopy: 'Copy',
    curlCopied: 'Copied!',
    saveAsVoice: 'Save as voice',
    saveAsVoiceTitle: 'Save generated audio as voice',
    saveAsVoiceSave: 'Save voice',
    saveAsVoiceSaving: 'Saving…',
    saveAsVoiceSuccess: 'Voice saved successfully',
    saveAsVoiceNoLanguage: 'No language set — you can assign one on the Voices page after saving.',
    downloadModalTitle: 'Download audio',
    downloadFilename: 'Filename',
    downloadFormat: 'Format',
    downloadSampleRate: 'Sample rate',
    downloadBitDepth: 'Bit depth',
    downloadBitRate: 'Bit rate',
    downloadEncoding: 'Encoding…',
    downloadBtn: 'Download',
    downloadWavBitDepth16: '16-bit PCM',
    downloadWavBitDepth32: '32-bit float',
  },
  model: {
    unknown: 'Unknown',
    loading: 'Loading…',
    ready: 'Ready',
    error: 'Error',
    load: 'Load',
    loaded: 'Loaded',
    cached: 'Cached',
    unloaded: 'Unloaded',
    unavailable: 'Unavailable',
    status: 'Service status',
  },
  history: {
    pageTitle: 'Synthesis History',
    noHistory: 'No synthesis history yet.',
    by: 'By',
    noAudio: 'No audio',
    download: 'Download',
    play: 'Play',
    archived: 'Archived',
    page: 'Page',
    of: 'of',
    fullText: 'Full text',
    colTime: 'Time',
    colVoice: 'Voice',
    colLang: 'Lang',
    colText: 'Text',
    colRaw: 'Raw text',
    colChars: 'Chars',
    colAudio: 'Audio',
  },
  limits: {
    usageLimits: 'Usage limits',
    daily: 'Today',
    weekly: 'Week',
    monthly: 'Month',
    chars: 'chars',
    groupDefault: 'group default: {n}',
    reached: 'Limit reached',
  },
}

const pl: typeof en = {
  nav: {
    synthesize: 'Syntezuj',
    voices: 'Głosy',
    history: 'Historia',
  },
  settings: {
    title: 'Ustawienia',
    appearance: 'Wygląd',
    theme: 'Motyw',
    themeDesc: 'Wybierz wygląd interfejsu',
    light: 'Jasny',
    dark: 'Ciemny',
    system: 'System',
    language: 'Język',
    languageDesc: 'Wybierz język interfejsu',
    sessionHistory: 'Historia sesji',
    sessionHistoryDesc: 'Liczba przebiegów syntezy widocznych w sesji',
    autoPlayStreaming: 'Autoodtwarzanie podczas generowania',
    autoPlayStreamingDesc: 'Strumieniuje audio do głośników podczas syntezy. Wyłącz, aby buforować bez dźwięku i odtwarzać ręcznie.',
    download: 'Pobieranie',
    downloadFormatLabel: 'Domyślny format',
    downloadFormatDesc: 'Domyślny format audio używany w oknie pobierania',
    downloadSampleRateLabel: 'Domyślna częstotliwość próbkowania',
    downloadSampleRateDesc: 'Domyślna częstotliwość próbkowania pobranego audio',
    downloadBitDepthLabel: 'Domyślna głębia bitowa WAV',
    downloadBitDepthDesc: 'Domyślna głębia bitowa dla plików WAV',
    downloadBitRateLabel: 'Domyślny bitrate MP3',
    downloadBitRateDesc: 'Domyślny bitrate dla plików MP3',
  },
  synthesis: {
    pageTitle: 'Synteza mowy',
    voiceLabel: 'Głos',
    noVoice: 'Bez głosu referencyjnego',
    styleLabel: 'Styl',
    noStyle: 'domyślny',
    languageLabel: 'Język',
    noLanguage: '— brak języka —',
    textLabel: 'Tekst',
    textPlaceholder: 'Wprowadź tekst do syntezy',
    normalizeText: 'Normalizuj tekst',
    normalizeHint: '(przetwarzanie wstępne LLM)',
    normalizedPreviewLabel: 'Znormalizowany tekst wysłany do TTS',
    ruleNormalizeText: 'Normalizacja słownikowa',
    ruleNormalizeHint: '(regułowe przetwarzanie wstępne)',
    ruleNormalizedPreviewLabel: 'Po normalizacji słownikowej',
    advanced: 'Opcje zaawansowane',
    speed: 'Szybkość',
    controlInstruction: 'Instrukcja sterowania',
    controlPlaceholder: 'np. Mów powoli i wyraźnie',
    modeLabel: 'Tryb głosowy',
    modeStrict: 'Ścisły',
    modeCreative: 'Kreatywny',
    btnNormalizing: 'Normalizowanie tekstu…',
    btnRuleNormalizing: 'Stosowanie reguł słownikowych…',
    btnSynthesizing: 'Syntezowanie…',
    btnLoading: 'Ładowanie modelu…',
    btnSynthesize: '▶ Syntezuj',
    errNormalizationFailed: 'Normalizacja nie powiodła się',
    errRuleNormalizationFailed: 'Normalizacja słownikowa nie powiodła się',
    errNormalizerUnavailable: 'Serwis normalizatora niedostępny',
    errRuleNormalizerUnavailable: 'Normalizator słownikowy niedostępny',
    errRuleNormalizationEmpty: 'Normalizacja słownikowa zwróciła pusty tekst — sprawdź dane wejściowe',
    errNormalizationEmpty: 'Normalizacja LLM zwróciła pusty tekst — sprawdź dane wejściowe',
    errRuleNormalizeNeedsLanguage: 'Normalizacja słownikowa wymaga obsługiwanego języka (polski, angielski, niemiecki, hiszpański lub ukraiński)',
    errServiceUnavailable: 'Serwis niedostępny',
    clearAll: 'Wyczyść wszystko',
  },
  voices: {
    pageTitle: 'Zarządzanie głosami',
    requiredRate: 'Wymagana częstotliwość próbkowania',
    dropZone: 'Upuść plik audio lub kliknij, aby przeglądać',
    resampling: 'Próbkowanie',
    resampled: 'Próbkowanie ukończone',
    nameLabel: 'Nazwa głosu (opcjonalnie)',
    namePlaceholder: 'np. Mówca 1',
    transcriptionLabel: 'Transkrypcja referencji (opcjonalnie)',
    transcriptionPlaceholder: 'Wprowadź dokładny tekst wypowiedziany w nagraniu…',
    defaultSpeedLabel: 'Domyślna szybkość (opcjonalnie)',
    defaultLanguageLabel: 'Domyślny język',
    noDefaultLanguage: 'Brak domyślnego języka',
    languageExclusive: 'Ekskluzywny dla języka',
    languageExclusiveHint: 'dostępny tylko gdy wybrany jest {lang}',
    btnUpload: 'Prześlij głos',
    btnUploading: 'Przesyłanie…',
    btnResampling: 'Próbkowanie…',
    btnCancel: 'Anuluj',
    noVoices: 'Brak przesłanych głosów',
    errResamplingFailed: 'Próbkowanie nie powiodło się — przekonwertuj plik ręcznie',
    errUploadFailed: 'Przesyłanie nie powiodło się',
    errUploadUnavailable: 'Przesyłanie nie powiodło się — serwis niedostępny',
    errPlaybackFailed: 'Odtwarzanie nie powiodło się',
    errPlaybackUnavailable: 'Odtwarzanie nie powiodło się — serwis niedostępny',
    ariaPlay: 'Odtwórz',
    ariaStop: 'Zatrzymaj',
    ariaDelete: 'Usuń',
    stylesBtn: 'Style',
    uploadTab: 'Prześlij plik',
    recordTab: 'Nagraj audio',
    recordStart: 'Kliknij, aby rozpocząć nagrywanie',
    recording: 'Nagrywanie',
    recordStop: 'Stop',
    recordProcessing: 'Przetwarzanie nagrania…',
    recordErrMic: 'Odmowa dostępu do mikrofonu',
    errAudioTooLong: 'Audio za długie — maksimum {max} s',
  },
  player: {
    buffering: 'Buforowanie…',
    streaming: 'Strumieniowanie',
    playback: 'Odtwarzanie',
    error: 'Błąd',
    downloadWav: 'Pobierz WAV',
    playbackInterrupted: 'Odtwarzanie przerwane',
    playFailed: 'Nie można odtworzyć dźwięku ({reason})',
    audioBlocked: 'Przeglądarka zablokowała dźwięk na żywo. Naciśnij ▶ po zakończeniu strumieniowania.',
    ariaPlay: 'Odtwórz',
    ariaPause: 'Pauza',
    curlTitle: 'Polecenie cURL',
    curlNote: 'Kieruje bezpośrednio do usługi TTS. Zamień localhost:8000 na adres swojego serwera.',
    curlCopy: 'Kopiuj',
    curlCopied: 'Skopiowano!',
    saveAsVoice: 'Zapisz jako głos',
    saveAsVoiceTitle: 'Zapisz wygenerowane audio jako głos',
    saveAsVoiceSave: 'Zapisz głos',
    saveAsVoiceSaving: 'Zapisywanie…',
    saveAsVoiceSuccess: 'Głos zapisany pomyślnie',
    saveAsVoiceNoLanguage: 'Brak ustawionego języka — możesz go przypisać na stronie Głosy po zapisaniu.',
    downloadModalTitle: 'Pobierz audio',
    downloadFilename: 'Nazwa pliku',
    downloadFormat: 'Format',
    downloadSampleRate: 'Częstotliwość próbkowania',
    downloadBitDepth: 'Głębia bitowa',
    downloadBitRate: 'Bitrate',
    downloadEncoding: 'Kodowanie…',
    downloadBtn: 'Pobierz',
    downloadWavBitDepth16: '16-bit PCM',
    downloadWavBitDepth32: '32-bit float',
  },
  model: {
    unknown: 'Nieznany',
    loading: 'Ładowanie…',
    ready: 'Gotowy',
    error: 'Błąd',
    load: 'Załaduj',
    loaded: 'Załadowany',
    cached: 'Zbuforowany',
    unloaded: 'Niezaładowany',
    unavailable: 'Niedostępny',
    status: 'Status usługi',
  },
  history: {
    pageTitle: 'Historia syntez',
    noHistory: 'Brak historii syntez.',
    by: 'Przez',
    noAudio: 'Brak audio',
    download: 'Pobierz',
    play: 'Odtwórz',
    archived: 'Zarchiwizowane',
    page: 'Strona',
    of: 'z',
    fullText: 'Pełny tekst',
    colTime: 'Czas',
    colVoice: 'Głos',
    colLang: 'Język',
    colText: 'Tekst',
    colRaw: 'Tekst oryginalny',
    colChars: 'Znaki',
    colAudio: 'Audio',
  },
  limits: {
    usageLimits: 'Limity użycia',
    daily: 'Dziś',
    weekly: 'Tydzień',
    monthly: 'Miesiąc',
    chars: 'znaków',
    groupDefault: 'domyślna grupy: {n}',
    reached: 'Osiągnięto limit',
  },
}

const de: typeof en = {
  nav: {
    synthesize: 'Synthetisieren',
    voices: 'Stimmen',
    history: 'Verlauf',
  },
  settings: {
    title: 'Einstellungen',
    appearance: 'Erscheinungsbild',
    theme: 'Design',
    themeDesc: 'Erscheinungsbild der Oberfläche wählen',
    light: 'Hell',
    dark: 'Dunkel',
    system: 'System',
    language: 'Sprache',
    languageDesc: 'Sprache der Benutzeroberfläche wählen',
    sessionHistory: 'Sitzungsverlauf',
    sessionHistoryDesc: 'Anzahl der Wellenformen in der aktuellen Sitzung',
    autoPlayStreaming: 'Autoplay während der Erzeugung',
    autoPlayStreamingDesc: 'Audio während der Synthese streamen. Deaktivieren, um stumm zu puffern und manuell abzuspielen.',
    download: 'Download',
    downloadFormatLabel: 'Standardformat',
    downloadFormatDesc: 'Standardaudioformat im Download-Dialog',
    downloadSampleRateLabel: 'Standard-Abtastrate',
    downloadSampleRateDesc: 'Standard-Abtastrate für heruntergeladenes Audio',
    downloadBitDepthLabel: 'Standard-WAV-Bittiefe',
    downloadBitDepthDesc: 'Standard-Bittiefe für WAV-Downloads',
    downloadBitRateLabel: 'Standard-MP3-Bitrate',
    downloadBitRateDesc: 'Standard-Bitrate für MP3-Downloads',
  },
  synthesis: {
    pageTitle: 'Sprachsynthese',
    voiceLabel: 'Stimme',
    noVoice: 'Keine Referenzstimme',
    styleLabel: 'Stil',
    noStyle: 'Standard',
    languageLabel: 'Sprache',
    noLanguage: '— keine Sprache —',
    textLabel: 'Text',
    textPlaceholder: 'Text zur Synthese eingeben',
    normalizeText: 'Text normalisieren',
    normalizeHint: '(LLM-Vorverarbeitung)',
    normalizedPreviewLabel: 'Normalisierter Text an TTS gesendet',
    ruleNormalizeText: 'Wörterbuch-Normalisierung',
    ruleNormalizeHint: '(regelbasierte Vorverarbeitung)',
    ruleNormalizedPreviewLabel: 'Nach Wörterbuch-Normalisierung',
    advanced: 'Erweiterte Optionen',
    speed: 'Geschwindigkeit',
    controlInstruction: 'Steuerungsanweisung',
    controlPlaceholder: 'z.B. Langsam und deutlich sprechen',
    modeLabel: 'Sprachmodus',
    modeStrict: 'Streng',
    modeCreative: 'Kreativ',
    btnNormalizing: 'Text wird normalisiert…',
    btnRuleNormalizing: 'Wörterbuchregeln werden angewendet…',
    btnSynthesizing: 'Synthese läuft…',
    btnLoading: 'Modell wird geladen…',
    btnSynthesize: '▶ Synthetisieren',
    errNormalizationFailed: 'Normalisierung fehlgeschlagen',
    errRuleNormalizationFailed: 'Wörterbuch-Normalisierung fehlgeschlagen',
    errNormalizerUnavailable: 'Normalisierungsdienst nicht verfügbar',
    errRuleNormalizerUnavailable: 'Wörterbuch-Normalisierer nicht verfügbar',
    errRuleNormalizationEmpty: 'Wörterbuch-Normalisierung lieferte leeren Text — bitte Eingabe prüfen',
    errNormalizationEmpty: 'LLM-Normalisierung lieferte leeren Text — bitte Eingabe prüfen',
    errRuleNormalizeNeedsLanguage: 'Wörterbuch-Normalisierung benötigt eine unterstützte Sprache (Polnisch, Englisch, Deutsch, Spanisch oder Ukrainisch)',
    errServiceUnavailable: 'Dienst nicht verfügbar',
    clearAll: 'Alle löschen',
  },
  voices: {
    pageTitle: 'Stimmenverwaltung',
    requiredRate: 'Erforderliche Abtastrate',
    dropZone: 'Audiodatei hier ablegen oder klicken zum Durchsuchen',
    resampling: 'Neuabtastung',
    resampled: 'Neuabtastung abgeschlossen',
    nameLabel: 'Stimmenname (optional)',
    namePlaceholder: 'z.B. Sprecher 1',
    transcriptionLabel: 'Referenztranskription (optional)',
    transcriptionPlaceholder: 'Geben Sie den genauen Text aus der Aufnahme ein…',
    defaultSpeedLabel: 'Standardgeschwindigkeit (optional)',
    defaultLanguageLabel: 'Standardsprache',
    noDefaultLanguage: 'Keine Standardsprache',
    languageExclusive: 'Sprachexklusiv',
    languageExclusiveHint: 'nur verfügbar wenn {lang} ausgewählt ist',
    btnUpload: 'Stimme hochladen',
    btnUploading: 'Wird hochgeladen…',
    btnResampling: 'Wird neu abgetastet…',
    btnCancel: 'Abbrechen',
    noVoices: 'Noch keine Stimmen hochgeladen',
    errResamplingFailed: 'Neuabtastung fehlgeschlagen — bitte Datei manuell konvertieren',
    errUploadFailed: 'Hochladen fehlgeschlagen',
    errUploadUnavailable: 'Hochladen fehlgeschlagen — Dienst nicht verfügbar',
    errPlaybackFailed: 'Wiedergabe fehlgeschlagen',
    errPlaybackUnavailable: 'Wiedergabe fehlgeschlagen — Dienst nicht verfügbar',
    ariaPlay: 'Abspielen',
    ariaStop: 'Stopp',
    ariaDelete: 'Löschen',
    stylesBtn: 'Stile',
    uploadTab: 'Datei hochladen',
    recordTab: 'Audio aufnehmen',
    recordStart: 'Zum Aufnehmen klicken',
    recording: 'Aufnahme',
    recordStop: 'Stopp',
    recordProcessing: 'Aufnahme wird verarbeitet…',
    recordErrMic: 'Mikrofonzugriff verweigert',
    errAudioTooLong: 'Audio zu lang — Maximum {max} s',
  },
  player: {
    buffering: 'Puffern…',
    streaming: 'Streamen',
    playback: 'Wiedergabe',
    error: 'Fehler',
    downloadWav: 'WAV herunterladen',
    playbackInterrupted: 'Wiedergabe unterbrochen',
    playFailed: 'Audio konnte nicht abgespielt werden ({reason})',
    audioBlocked: 'Der Browser hat die Live-Wiedergabe blockiert. Drücke ▶, wenn das Streaming beendet ist.',
    ariaPlay: 'Abspielen',
    ariaPause: 'Pause',
    curlTitle: 'cURL-Befehl',
    curlNote: 'Richtet sich direkt an den TTS-Dienst. localhost:8000 durch Ihre Serveradresse ersetzen.',
    curlCopy: 'Kopieren',
    curlCopied: 'Kopiert!',
    saveAsVoice: 'Als Stimme speichern',
    saveAsVoiceTitle: 'Generiertes Audio als Stimme speichern',
    saveAsVoiceSave: 'Stimme speichern',
    saveAsVoiceSaving: 'Speichern…',
    saveAsVoiceSuccess: 'Stimme erfolgreich gespeichert',
    saveAsVoiceNoLanguage: 'Keine Sprache gesetzt — kann nach dem Speichern auf der Seite „Stimmen" zugewiesen werden.',
    downloadModalTitle: 'Audio herunterladen',
    downloadFilename: 'Dateiname',
    downloadFormat: 'Format',
    downloadSampleRate: 'Abtastrate',
    downloadBitDepth: 'Bittiefe',
    downloadBitRate: 'Bitrate',
    downloadEncoding: 'Kodierung…',
    downloadBtn: 'Herunterladen',
    downloadWavBitDepth16: '16-Bit PCM',
    downloadWavBitDepth32: '32-Bit Float',
  },
  model: {
    unknown: 'Unbekannt',
    loading: 'Wird geladen…',
    ready: 'Bereit',
    error: 'Fehler',
    load: 'Laden',
    loaded: 'Geladen',
    cached: 'Gecacht',
    unloaded: 'Nicht geladen',
    unavailable: 'Nicht verfügbar',
    status: 'Dienststatus',
  },
  history: {
    pageTitle: 'Syntheseverlauf',
    noHistory: 'Noch kein Syntheseverlauf.',
    by: 'Von',
    noAudio: 'Kein Audio',
    download: 'Herunterladen',
    play: 'Abspielen',
    archived: 'Archiviert',
    page: 'Seite',
    of: 'von',
    fullText: 'Vollständiger Text',
    colTime: 'Zeit',
    colVoice: 'Stimme',
    colLang: 'Sprache',
    colText: 'Text',
    colRaw: 'Originaltext',
    colChars: 'Zeichen',
    colAudio: 'Audio',
  },
  limits: {
    usageLimits: 'Nutzungslimits',
    daily: 'Heute',
    weekly: 'Woche',
    monthly: 'Monat',
    chars: 'Zeichen',
    groupDefault: 'Gruppenstandard: {n}',
    reached: 'Limit erreicht',
  },
}

const uk: typeof en = {
  nav: {
    synthesize: 'Синтезувати',
    voices: 'Голоси',
    history: 'Історія',
  },
  settings: {
    title: 'Налаштування',
    appearance: 'Зовнішній вигляд',
    theme: 'Тема',
    themeDesc: 'Виберіть вигляд інтерфейсу',
    light: 'Світла',
    dark: 'Темна',
    system: 'Система',
    language: 'Мова',
    languageDesc: 'Виберіть мову інтерфейсу',
    sessionHistory: 'Історія сесії',
    sessionHistoryDesc: 'Кількість форм хвиль у поточній сесії синтезу',
    autoPlayStreaming: 'Автовідтворення під час генерування',
    autoPlayStreamingDesc: 'Транслювати аудіо на динаміки під час синтезу. Вимкніть, щоб буферизувати беззвучно та відтворювати вручну.',
    download: 'Завантаження',
    downloadFormatLabel: 'Формат за замовчуванням',
    downloadFormatDesc: 'Формат аудіо за замовчуванням у діалозі завантаження',
    downloadSampleRateLabel: 'Частота дискретизації за замовчуванням',
    downloadSampleRateDesc: 'Частота дискретизації для завантажуваного аудіо',
    downloadBitDepthLabel: 'Глибина біт WAV за замовчуванням',
    downloadBitDepthDesc: 'Глибина біт для завантаження WAV',
    downloadBitRateLabel: 'Бітрейт MP3 за замовчуванням',
    downloadBitRateDesc: 'Бітрейт для завантаження MP3',
  },
  synthesis: {
    pageTitle: 'Синтез мовлення',
    voiceLabel: 'Голос',
    noVoice: 'Без референсного голосу',
    styleLabel: 'Стиль',
    noStyle: 'стандартний',
    languageLabel: 'Мова',
    noLanguage: '— без мови —',
    textLabel: 'Текст',
    textPlaceholder: 'Введіть текст для синтезу',
    normalizeText: 'Нормалізувати текст',
    normalizeHint: '(попередня обробка LLM)',
    normalizedPreviewLabel: 'Нормалізований текст надіслано до TTS',
    ruleNormalizeText: 'Словникова нормалізація',
    ruleNormalizeHint: '(попередня обробка на основі правил)',
    ruleNormalizedPreviewLabel: 'Після словникової нормалізації',
    advanced: 'Розширені параметри',
    speed: 'Швидкість',
    controlInstruction: 'Інструкція керування',
    controlPlaceholder: 'напр. Говоріть повільно і чітко',
    modeLabel: 'Режим голосу',
    modeStrict: 'Суворий',
    modeCreative: 'Творчий',
    btnNormalizing: 'Нормалізація тексту…',
    btnRuleNormalizing: 'Застосування словникових правил…',
    btnSynthesizing: 'Синтезування…',
    btnLoading: 'Завантаження моделі…',
    btnSynthesize: '▶ Синтезувати',
    errNormalizationFailed: 'Нормалізація не вдалася',
    errRuleNormalizationFailed: 'Словникова нормалізація не вдалася',
    errNormalizerUnavailable: 'Сервіс нормалізатора недоступний',
    errRuleNormalizerUnavailable: 'Словниковий нормалізатор недоступний',
    errRuleNormalizationEmpty: 'Словникова нормалізація повернула порожній текст — перевірте введені дані',
    errNormalizationEmpty: 'LLM-нормалізація повернула порожній текст — перевірте введені дані',
    errRuleNormalizeNeedsLanguage: 'Словникова нормалізація потребує підтримуваної мови (польська, англійська, німецька, іспанська або українська)',
    errServiceUnavailable: 'Сервіс недоступний',
    clearAll: 'Очистити все',
  },
  voices: {
    pageTitle: 'Керування голосами',
    requiredRate: 'Необхідна частота дискретизації',
    dropZone: 'Перетягніть аудіофайл або клацніть для перегляду',
    resampling: 'Ресемплювання',
    resampled: 'Ресемплювання завершено',
    nameLabel: 'Назва голосу (необов\'язково)',
    namePlaceholder: 'напр. Диктор 1',
    transcriptionLabel: 'Транскрипція референсу (необов\'язково)',
    transcriptionPlaceholder: 'Введіть точний текст, вимовлений у записі…',
    defaultSpeedLabel: 'Стандартна швидкість (необов\'язково)',
    defaultLanguageLabel: 'Мова за замовчуванням',
    noDefaultLanguage: 'Без мови за замовчуванням',
    languageExclusive: 'Виключно для мови',
    languageExclusiveHint: 'доступний лише коли вибрано {lang}',
    btnUpload: 'Завантажити голос',
    btnUploading: 'Завантаження…',
    btnResampling: 'Ресемплювання…',
    btnCancel: 'Скасувати',
    noVoices: 'Ще немає завантажених голосів',
    errResamplingFailed: 'Ресемплювання не вдалося — перетворіть файл вручну',
    errUploadFailed: 'Завантаження не вдалося',
    errUploadUnavailable: 'Завантаження не вдалося — сервіс недоступний',
    errPlaybackFailed: 'Відтворення не вдалося',
    errPlaybackUnavailable: 'Відтворення не вдалося — сервіс недоступний',
    ariaPlay: 'Відтворити',
    ariaStop: 'Зупинити',
    ariaDelete: 'Видалити',
    stylesBtn: 'Стилі',
    uploadTab: 'Завантажити файл',
    recordTab: 'Записати аудіо',
    recordStart: 'Натисніть, щоб почати запис',
    recording: 'Запис',
    recordStop: 'Стоп',
    recordProcessing: 'Обробка запису…',
    recordErrMic: 'Відмовлено в доступі до мікрофона',
    errAudioTooLong: 'Аудіо занадто довге — максимум {max} с',
  },
  player: {
    buffering: 'Буферизація…',
    streaming: 'Потокове відтворення',
    playback: 'Відтворення',
    error: 'Помилка',
    downloadWav: 'Завантажити WAV',
    playbackInterrupted: 'Відтворення перервано',
    playFailed: 'Не вдалося відтворити аудіо ({reason})',
    audioBlocked: 'Браузер заблокував звук наживо. Натисніть ▶ після завершення потокової передачі.',
    ariaPlay: 'Відтворити',
    ariaPause: 'Пауза',
    curlTitle: 'Команда cURL',
    curlNote: 'Звертається безпосередньо до сервісу TTS. Замініть localhost:8000 на адресу вашого сервера.',
    curlCopy: 'Копіювати',
    curlCopied: 'Скопійовано!',
    saveAsVoice: 'Зберегти як голос',
    saveAsVoiceTitle: 'Зберегти згенероване аудіо як голос',
    saveAsVoiceSave: 'Зберегти голос',
    saveAsVoiceSaving: 'Збереження…',
    saveAsVoiceSuccess: 'Голос успішно збережено',
    saveAsVoiceNoLanguage: 'Мова не встановлена — можна призначити на сторінці «Голоси» після збереження.',
    downloadModalTitle: 'Завантажити аудіо',
    downloadFilename: 'Ім\'я файлу',
    downloadFormat: 'Формат',
    downloadSampleRate: 'Частота дискретизації',
    downloadBitDepth: 'Глибина біт',
    downloadBitRate: 'Бітрейт',
    downloadEncoding: 'Кодування…',
    downloadBtn: 'Завантажити',
    downloadWavBitDepth16: '16-біт PCM',
    downloadWavBitDepth32: '32-біт float',
  },
  model: {
    unknown: 'Невідомо',
    loading: 'Завантаження…',
    ready: 'Готово',
    error: 'Помилка',
    load: 'Завантажити',
    loaded: 'Завантажено',
    cached: 'У кеші',
    unloaded: 'Не завантажено',
    unavailable: 'Недоступний',
    status: 'Статус сервісу',
  },
  history: {
    pageTitle: 'Історія синтезів',
    noHistory: 'Ще немає історії синтезів.',
    by: 'Від',
    noAudio: 'Немає аудіо',
    download: 'Завантажити',
    play: 'Відтворити',
    archived: 'Архівовано',
    page: 'Сторінка',
    of: 'з',
    fullText: 'Повний текст',
    colTime: 'Час',
    colVoice: 'Голос',
    colLang: 'Мова',
    colText: 'Текст',
    colRaw: 'Оригінальний текст',
    colChars: 'Знаки',
    colAudio: 'Аудіо',
  },
  limits: {
    usageLimits: 'Ліміти використання',
    daily: 'Сьогодні',
    weekly: 'Тиждень',
    monthly: 'Місяць',
    chars: 'символів',
    groupDefault: 'типово для групи: {n}',
    reached: 'Ліміт досягнуто',
  },
}

export type Translations = typeof en

export const TRANSLATIONS: Record<Locale, Translations> = { en, pl, de, uk }
